/*
 *
 * ShelvesRibbon
 *
 */

import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useInjectReducer } from 'utils/injectReducer';
import { createStructuredSelector } from 'reselect';
import reducer from 'containers/Ribbon/ShelvesRibbon/reducer';
import { RootState } from 'containers/Ribbon/ShelvesRibbon/types';
import { StyledRibbonWrapper } from 'containers/Ribbon/StyledRibbonWrapper';
import RibbonSection from 'containers/Ribbon/RibbonSection';
import ShelvesSelection from 'containers/Ribbon/ShelvesRibbon/ShelvesSelection/ShelvesSelection';
import { makeSelectShelves } from 'containers/Configurator/selectors/furniture';
import { Shelf } from 'lib/modelBuilder/entities/Shelf';
import sizeDepth from 'assets/images/size/depth.svg';
import { FiChevronDown } from 'react-icons/fi';
import {
  makeSelectCommonHeight,
  makeSelectHaveOneDoor,
  makeSelectHaveOneDrawer,
  makeSelectMarkHeight,
} from 'containers/Ribbon/ShelvesRibbon/selectors';
import {
  addShelf,
  setShelfHeight,
} from 'containers/Configurator/actions/furniture';
import ShelvesHeightSelector from 'containers/Ribbon/ShelvesRibbon/ShelvesHeightSelector/ShelvesHeightSelector';
import ActionShelfRibbonSection from 'containers/Ribbon/ShelvesRibbon/ActionShelfRibbonSection';
import ShelfCompositionSelector from 'containers/Ribbon/ShelvesRibbon/ShelfCompositionSelector';
import { useMedia } from 'use-media';
import MobileMenuShelves from 'containers/Ribbon/ShelvesRibbon/MobileMenuShelves';
import {
  makeSelectShelvesLevelSelected,
  makeSelectShelvesSelected,
} from 'containers/Configurator/selectors/selection';
import { useInjectSaga } from 'utils/injectSaga';
import saga from './saga';
import ColorSection from 'containers/Ribbon/ShelvesRibbon/ColorsSection';
import { useTranslation } from 'react-i18next';
import addAShelf from 'assets/images/ribbons/cabinetRibbon/addShelf.svg';
import addAShelfSmall from 'assets/images/ribbons/cabinetRibbon/addShelfSmall.svg';
import Button from 'components/Button';
import { shelvesSelectionMenuAction } from './actions';
import { makeSelectFullDrawersSelection } from '../../DrawersPanel/selectors';
import { makeSelectCanAddIs } from './ColorsSection/selectors';
import AccessoriesSection from './AccessoriesSection';
import { displayDimensionAction } from '../../Configurator/actions/general';
import ShelvesOptions from './ShelvesOptions';

interface StateProps {
  haveOneDrawer: boolean;
  haveOneDoor: boolean;
  shelvesLevelSelected: number[];
  shelvesSelected: Shelf[];
  shelvesCabinet: Shelf[];
  commomHeight: number | undefined;
  isAllSelectionDrawers: boolean;
  canAddIs: boolean;
  marksHeight: any[];
}

const stateSelector = createStructuredSelector<RootState, StateProps>({
  haveOneDrawer: makeSelectHaveOneDrawer(),
  haveOneDoor: makeSelectHaveOneDoor(),
  shelvesLevelSelected: makeSelectShelvesLevelSelected(),
  shelvesSelected: makeSelectShelvesSelected(),
  shelvesCabinet: makeSelectShelves(),
  commomHeight: makeSelectCommonHeight(),
  isAllSelectionDrawers: makeSelectFullDrawersSelection(),
  canAddIs: makeSelectCanAddIs(),
  marksHeight: makeSelectMarkHeight(),
});

const key = 'shelvesRibbon';

function ShelvesRibbon() {
  useInjectReducer({ key: key, reducer: reducer });
  useInjectSaga({ key: key, saga: saga });
  const dispatch = useDispatch();
  const {
    haveOneDrawer,
    haveOneDoor,
    shelvesLevelSelected,
    shelvesCabinet,
    shelvesSelected,
    commomHeight,
    isAllSelectionDrawers,
    canAddIs,
    marksHeight,
  } = useSelector(stateSelector);
  const isMobile = useMedia({ maxWidth: 1023 });
  const { t } = useTranslation();

  const [isSelectionVisible, setSelectionVisible] = useState(false);
  const isXLargeScreen = useMedia({ minWidth: 1280 });

  const updateSelectedHeight = (value: number, preview: boolean): void => {
    dispatch(setShelfHeight(value, shelvesLevelSelected, preview));
  };

  const displaySelection = (value: boolean) => {
    dispatch(shelvesSelectionMenuAction(true));
    setSelectionVisible(value);
  };

  const onSelectionBlue = () => {
    setSelectionVisible(false);
  };

  function handleAddShelf() {
    dispatch(addShelf());
  }

  useEffect(() => {
    dispatch(shelvesSelectionMenuAction(isSelectionVisible));
  }, [isSelectionVisible]);

  const showShelfActionMenu =
    shelvesCabinet.length > 1 && shelvesSelected.length === 1;
  const showIS = !isAllSelectionDrawers && canAddIs;
  const showHandles = haveOneDoor || haveOneDrawer;

  if (!isMobile) {
    return (
      <StyledRibbonWrapper>
        <RibbonSection className="tuto-step-shelves-selection" border>
          {shelvesCabinet.length > 1 && (
            <>
              {!isSelectionVisible ? (
                <div className="ml-2">
                  <button
                    className="flex items-center"
                    onClick={() => {
                      displaySelection(true);
                      dispatch(displayDimensionAction(false));
                    }}
                  >
                    <div className="ml-2 mr-2 text-gray-800">
                      {t('Selection')}
                    </div>
                    <FiChevronDown className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <ShelvesSelection
                  shelvesCabinet={shelvesCabinet}
                  selections={shelvesLevelSelected}
                  onBlur={onSelectionBlue}
                />
              )}
            </>
          )}

          <div className={isSelectionVisible ? 'lg:-mt-2' : ''}>
            <Button
              title={t('Add')}
              onClick={handleAddShelf}
              img={shelvesCabinet.length > 1 ? addAShelfSmall : addAShelf}
              small={shelvesCabinet.length > 1}
            />
          </div>
        </RibbonSection>
        <div className="h-20 border-l border-gray-400" />

        {showShelfActionMenu && (
          <>
            <RibbonSection title={t('Shelf')} border>
              <ActionShelfRibbonSection shelf={shelvesSelected[0]} />
            </RibbonSection>
            <div className="h-20 border-l border-gray-400" />
          </>
        )}

        <RibbonSection
          title={t('Height')}
          className="tuto-step-shelves-height"
          border
        >
          <div className={`${showShelfActionMenu ? 'w-40' : 'w-56'} xl:w-80`}>
            <ShelvesHeightSelector
              commonHeight={commomHeight}
              icon={sizeDepth}
              handleInput={updateSelectedHeight}
              min={marksHeight[0].value}
              max={marksHeight[marksHeight.length - 1].value}
              marks={marksHeight}
              allShelves={shelvesCabinet.length === shelvesSelected.length}
              shelvesSelected={shelvesSelected}
              custom={false}
            />
          </div>
        </RibbonSection>
        <div className="h-20 border-l border-gray-400" />

        <RibbonSection
          title={t('Composition')}
          className="tuto-step-shelves-composition"
          border
        >
          <ShelfCompositionSelector />
        </RibbonSection>
        <div className="h-20 border-l border-gray-400" />

        <RibbonSection
          title={t('Parts and colors')}
          className="tuto-step-shelves-parts"
          border
        >
          <ColorSection />
        </RibbonSection>
        <AccessoriesSection />
        <ShelvesOptions />
      </StyledRibbonWrapper>
    );
  } else {
    return (
      <MobileMenuShelves
        commonHeight={commomHeight}
        shelvesCabinet={shelvesCabinet}
        shelvesSelected={shelvesSelected}
        updateSelectedHeight={updateSelectedHeight}
      />
    );
  }
}

export default ShelvesRibbon;
